import React, { useState, useRef, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { updateProfile, getStaffById, getMyPresence, setPresenceOverride, removePresenceOverride } from '../api/apiClient';
import './AccountSettings.css';

const getCroppedImg = (imageSrc, pixelCrop) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const size = Math.min(pixelCrop.width, pixelCrop.height, 400);
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', 0.88));
    };
    image.src = imageSrc;
  });

const OFFICES = ['Chicago', 'Delaware', 'Washington D.C.'];
const STATUS_LABEL = { office: 'Office', home: 'Remote', out: 'Away' };
const STATUS_COLOR = { office: '#22c55e', home: '#3b82f6', out: '#a78bfa' };

const CATEGORIES = ['Partner', 'Of Counsel/Senior Counsel', 'Associate', 'Staff'];

const formatPhone = (val) => {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0,3)}-${digits.slice(3)}`;
  return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
};

const AccountSettings = ({ currentUser, onClose, onUserUpdate }) => {
  const [name,          setName]          = useState(currentUser.name || '');
  const [avatar,        setAvatar]        = useState(currentUser.avatar || null);
  const [avatarChanged, setAvatarChanged] = useState(false);
  const [roleCategory,  setRoleCategory]  = useState('');
  const [customTitle,   setCustomTitle]   = useState('');
  const [officePhone,   setOfficePhone]   = useState('');
  const [phoneExt,      setPhoneExt]      = useState('');
  const [mobilePhone,   setMobilePhone]   = useState('');
  const [bio,           setBio]           = useState('');
  const [officeLocation, setOfficeLocation] = useState('Chicago');
  const [birthMonth,    setBirthMonth]    = useState('');
  const [birthDay,      setBirthDay]      = useState('');
  const [saving,        setSaving]        = useState(false);
  const [profileMsg,    setProfileMsg]    = useState(null);

  // Today's status (set by firm wifi or the daily popup)
  const [todayOverride, setTodayOverride] = useState(null);

  // Browser notifications
  const [browserNotifEnabled, setBrowserNotifEnabled] = useState(false);
  const [browserNotifSupported] = useState('Notification' in window);

  // Out of office
  const [outOfOfficeReturn, setOutOfOfficeReturn] = useState(null);
  const [selectedReturnDate, setSelectedReturnDate] = useState('');

  // Fetch full profile on open
  useEffect(() => {
    getStaffById(currentUser.id).then(res => {
      const d = res.data;
      setName(d.name || '');
      setAvatar(d.avatar || null);
      const cat = d.role_category || 'Staff';
      setRoleCategory(cat);
      setCustomTitle(d.title || '');
      setOfficePhone(d.phone || '');
      setPhoneExt(d.phone_extension || '');
      setMobilePhone(d.mobile_phone || '');
      setBio(d.bio || '');
      setOfficeLocation(d.office || 'Chicago');
      if (d.birthday) {
        const [m, day] = d.birthday.split('-');
        setBirthMonth(m || '');
        setBirthDay(day || '');
      }
      if (d.out_of_office_until) {
        setOutOfOfficeReturn(new Date(d.out_of_office_until));
        setSelectedReturnDate(d.out_of_office_until);
      }
    }).catch(() => {});

    getMyPresence().then(res => {
      setTodayOverride(res.data.today_status);
    }).catch(() => {});

    // Check browser notification permission
    if (browserNotifSupported && Notification.permission === 'granted') {
      setBrowserNotifEnabled(true);
    }
  }, [currentUser.id, browserNotifSupported]);

  const fileRef = useRef(null);
  const [cropSrc,            setCropSrc]            = useState(null);
  const [crop,               setCrop]               = useState({ x: 0, y: 0 });
  const [zoom,               setZoom]               = useState(1);
  const [croppedAreaPixels,  setCroppedAreaPixels]  = useState(null);

  const handleAvatarPick = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setProfileMsg({ type: 'err', text: 'Image must be under 10 MB' }); return; }
    const reader = new FileReader();
    reader.onload = ev => {
      setCropSrc(ev.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropConfirm = useCallback(async () => {
    if (!croppedAreaPixels) return;
    const cropped = await getCroppedImg(cropSrc, croppedAreaPixels);
    setAvatar(cropped);
    setAvatarChanged(true);
    setCropSrc(null);
  }, [cropSrc, croppedAreaPixels]);

  const handleSaveProfile = async () => {
    if (!name.trim()) { setProfileMsg({ type: 'err', text: 'Name cannot be empty' }); return; }
    setSaving(true); setProfileMsg(null);
    try {
      await updateProfile(currentUser.id, {
        name: name.trim(),
        avatar,
        avatar_changed: avatarChanged,
        role_category: roleCategory,
        title: customTitle.trim(),
        phone: officePhone.trim(),
        phone_extension: phoneExt.trim(),
        mobile_phone: mobilePhone.trim(),
        bio: bio.trim(),
        office: officeLocation,
        birthday: birthMonth && birthDay ? `${birthMonth}-${birthDay.padStart(2, '0')}` : null,
        requester_id: currentUser.id,
      });
      onUserUpdate({ name: name.trim(), avatar });
      setAvatarChanged(false);
      setProfileMsg({ type: 'ok', text: 'Profile updated.' });
    } catch (err) {
      setProfileMsg({ type: 'err', text: err.response?.data?.error || 'Failed to save profile.' });
    } finally { setSaving(false); }
  };

  const handleTodayOverride = async (status) => {
    try {
      if (status === null) {
        await removePresenceOverride({ staff_id: currentUser.id });
        setTodayOverride(null);
        setOutOfOfficeReturn(null);
      } else {
        await setPresenceOverride({
          staff_id: currentUser.id,
          status,
          out_of_office_until: status === 'out' && outOfOfficeReturn ? outOfOfficeReturn.toISOString().split('T')[0] : null
        });
        setTodayOverride(status);
      }
    } catch { /* silently ignore */ }
  };

  const handleBrowserNotifications = async () => {
    if (!browserNotifSupported) return;
    if (browserNotifEnabled) {
      setBrowserNotifEnabled(false);
    } else {
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          setBrowserNotifEnabled(true);
          new Notification('Notifications Enabled', {
            body: 'You will now receive browser notifications for mentions and new announcements.',
            icon: '/logos/esbrook_pc_logo_web_no_bg_black.png'
          });
        }
      } catch (err) {
        console.error('Notification permission error:', err);
      }
    }
  };

  const initials = name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <>
    {cropSrc && (
      <div className="crop-overlay">
        <div className="crop-modal">
          <p className="crop-title">Drag & scroll to crop your photo</p>
          <div className="crop-area">
            <Cropper
              image={cropSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
            />
          </div>
          <div className="crop-zoom-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
            <input className="crop-zoom-slider" type="range" min={1} max={3} step={0.01} value={zoom} onChange={e => setZoom(Number(e.target.value))} />
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
          </div>
          <div className="crop-actions">
            <button className="crop-cancel-btn" onClick={() => setCropSrc(null)}>Cancel</button>
            <button className="crop-confirm-btn" onClick={handleCropConfirm}>Apply</button>
          </div>
        </div>
      </div>
    )}
    <div className="as-overlay" onClick={onClose}>
      <div className="as-modal" onClick={e => e.stopPropagation()}>
        <div className="as-header">
          <h2>Account Settings</h2>
          <button className="as-close" onClick={onClose}>✕</button>
        </div>

        <div className="as-body">
          {/* Avatar */}
          <div className="as-avatar-wrap">
            <button className="as-avatar-btn" onClick={() => fileRef.current?.click()}>
              {avatar
                ? <img src={avatar} alt="avatar" className="as-avatar-img" />
                : <span className="as-avatar-initials">{initials}</span>}
              <div className="as-avatar-overlay">Change</div>
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleAvatarPick} />
            <div className="as-avatar-hints">
              <p className="as-avatar-hint">Click to upload photo</p>
              {avatar && (
                <button
                  type="button"
                  className="as-avatar-remove"
                  onClick={() => { setAvatar(null); setAvatarChanged(true); }}
                >
                  Remove photo
                </button>
              )}
            </div>
          </div>

          {/* Name */}
          <div className="as-field">
            <label>Display Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" />
          </div>

          {/* Role */}
          <div className="as-field">
            <label>Role</label>
            <select
              value={roleCategory}
              onChange={e => { setRoleCategory(e.target.value); setCustomTitle(''); }}
              className="as-select"
            >
              <option value="" disabled>Select…</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {roleCategory && (
            <div className="as-field">
              <label>Job Title</label>
              <input
                type="text"
                value={customTitle}
                onChange={e => setCustomTitle(e.target.value)}
                placeholder={{
                  'Partner':                   'e.g. Partner, Managing Partner',
                  'Of Counsel/Senior Counsel': 'e.g. Of Counsel, Senior Counsel',
                  'Associate':                 'e.g. Associate, Senior Associate',
                  'Staff':                     'e.g. Paralegal, Office Manager, Intern',
                }[roleCategory]}
              />
            </div>
          )}

          {/* Phones */}
          <div className="as-field">
            <label>Office Phone</label>
            <div className="as-phone-row">
              <input type="tel" value={officePhone} onChange={e => setOfficePhone(formatPhone(e.target.value))} placeholder="555-867-5309" className="as-phone-main" />
              <span className="as-ext-label">Ext.</span>
              <input type="text" value={phoneExt} onChange={e => setPhoneExt(e.target.value)} placeholder="123" className="as-phone-ext" />
            </div>
          </div>
          <div className="as-field">
            <label>Mobile Phone</label>
            <input type="tel" value={mobilePhone} onChange={e => setMobilePhone(formatPhone(e.target.value))} placeholder="555-867-5309" />
          </div>

          <div className="as-field">
            <label>Office Location</label>
            <select value={officeLocation} onChange={e => setOfficeLocation(e.target.value)} className="as-select">
              {OFFICES.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          <div className="as-field">
            <label>Birthday <span className="as-field-hint">Used for birthday mentions</span></label>
            <div className="as-birthday-row">
              <select value={birthMonth} onChange={e => { setBirthMonth(e.target.value); setBirthDay(''); }} className="as-select as-birthday-month">
                <option value="">Month</option>
                {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, i) => (
                  <option key={m} value={m}>{['January','February','March','April','May','June','July','August','September','October','November','December'][i]}</option>
                ))}
              </select>
              <select value={birthDay} onChange={e => setBirthDay(e.target.value)} className="as-select as-birthday-day" disabled={!birthMonth}>
                <option value="">Day</option>
                {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0')).map(d => (
                  <option key={d} value={d}>{parseInt(d)}</option>
                ))}
              </select>
              {(birthMonth || birthDay) && (
                <button type="button" className="as-birthday-clear" onClick={() => { setBirthMonth(''); setBirthDay(''); }}>✕</button>
              )}
            </div>
          </div>

          {profileMsg && <p className={`as-msg ${profileMsg.type}`}>{profileMsg.text}</p>}
          <button className="as-save-btn" onClick={handleSaveProfile} disabled={saving}>
            {saving ? 'Saving…' : 'Save Profile'}
          </button>

          {/* Today's status — set automatically by the firm wifi, or here/the daily popup */}
          <div className="as-divider"><span>Today's Status</span></div>

          <div className="as-field">
            <label>Today's Status <span className="as-hint-inline">(auto-set when you're on the office wifi)</span></label>
            <div className="as-today-row">
              {['office', 'home', 'out'].map(s => {
                const isActive = todayOverride === s;
                return (
                  <button
                    key={s}
                    type="button"
                    className={`as-status-btn${isActive ? ' as-status-btn--active' : ''}`}
                    style={isActive ? { background: STATUS_COLOR[s], color: '#fff', borderColor: STATUS_COLOR[s] } : {}}
                    onClick={() => handleTodayOverride(isActive && todayOverride ? null : s)}
                  >
                    {s === 'office' ? '🏢' : s === 'home' ? '🏠' : '🌙'} {STATUS_LABEL[s]}
                  </button>
                );
              })}
            </div>
            {todayOverride === 'out' && !outOfOfficeReturn && (
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f8f9fb', borderRadius: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#272e40' }}>When will you be back?</label>
                <input
                  type="date"
                  value={selectedReturnDate}
                  onChange={e => setSelectedReturnDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{ width: '100%', padding: '10px 14px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '20px', boxSizing: 'border-box', marginBottom: '10px' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => { handleTodayOverride(null); setSelectedReturnDate(''); }}
                    style={{ flex: 1, padding: '8px', backgroundColor: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (selectedReturnDate) {
                        const returnDate = new Date(selectedReturnDate);
                        setOutOfOfficeReturn(returnDate);
                        await setPresenceOverride({
                          staff_id: currentUser.id,
                          status: 'out',
                          out_of_office_until: selectedReturnDate
                        });
                        setSelectedReturnDate('');
                      }
                    }}
                    style={{ flex: 1, padding: '8px', backgroundColor: '#272e40', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                  >
                    Set Out of Office
                  </button>
                </div>
              </div>
            )}
            {outOfOfficeReturn && (
              <p className="as-hint">Out of office until <b>{new Date(outOfOfficeReturn).toLocaleDateString()}</b> — <button type="button" className="as-link-btn" onClick={() => { setOutOfOfficeReturn(null); handleTodayOverride(null); }}>back early</button></p>
            )}
          </div>


          {browserNotifSupported && (
            <>
              <div className="as-divider"><span>Notifications</span></div>
              <label className="as-notif-row">
                <div className="as-notif-text">
                  <span className="as-notif-label">Browser Notifications</span>
                  <span className="as-notif-desc">Get alerts for mentions and announcements even when you're away</span>
                </div>
                <div
                  className={`as-toggle ${browserNotifEnabled ? 'as-toggle--on' : ''}`}
                  onClick={handleBrowserNotifications}
                  role="switch"
                  aria-checked={browserNotifEnabled}
                  tabIndex={0}
                  onKeyDown={e => e.key === ' ' && handleBrowserNotifications()}
                >
                  <span className="as-toggle-thumb" />
                </div>
              </label>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default AccountSettings;
